import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Button, cn, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@atrvd/saas-ui'
import type { CashPositionSummary } from '@/hooks/cashPositionSummaries'
import type { CashPositionMultiPeriodColumn } from '@/hooks/useCashPositionSummaryMultiPeriod'
import type { CashPositionPeriod } from '@/lib/date-utils'
import {
  buildBalancesRows,
  buildLocRows,
  buildProjectionRows,
  formatCell,
  type SectionTone,
  type SummaryRow,
} from './cashPositionRows'
import { TOTAL_COLUMN_KEY, type CashPositionWarningMap } from './cashPositionWarnings'
import { DataWarningIcon } from './DataWarningIcon'
import { ViewTransactionsIcon } from './ViewTransactionsIcon'

/**
 * Props for the multi-period Cash Position Summary grid (DCP-144).
 */
interface CashPositionSummaryGridProps {
  /** Ordered data columns — facilities and pack subtotals, each per-period.
   *  Consumes the hook's column type directly so the two can't structurally drift. */
  columns: CashPositionMultiPeriodColumn[]
  /** Field-wise Combined Total column, per-period. */
  combinedTotal: { periods: CashPositionSummary[] }
  /**
   * Whether to render the frozen "Combined Total" column (DCP-293). Hidden when
   * the filter selection narrows the report to a SINGLE pack or a single
   * facility, where the combined total would merely duplicate that pack's
   * subtotal column / that facility's own column. Defaults to `true`.
   */
  showCombinedTotal?: boolean
  /** The five period descriptors (drives each projection block's heading). */
  periodMeta: CashPositionPeriod[]
  /**
   * Which period indices to render, in order (e.g. [0,1,2,3,4] for "All", or a
   * single index like [2] for "2 Weeks Out"). The balances block always renders
   * once regardless; only the projection blocks honour this list.
   */
  visiblePeriodIndices: number[]
  /**
   * Opens the "View bank transactions" drill-down for a facility/pack column's
   * LOC Usage cell (DCP-169). Called with the clicked column so the page can
   * scope the transactions by id (facility → `column.key`; pack → the pack_id in
   * `column.key`), plus the CELL'S VALUE — the LOC Usage figure the user actually
   * drilled from. The modal compares it against the transactions it lists, but
   * (DCP-333) only warns when that list comes back EMPTY under a figure that
   * implies transactions exist — a partially-truncated feed is caught separately
   * by the hook's own row-count guard, not by this comparison. The value is
   * passed from here rather than re-derived by the page because only this
   * component knows which period's cell was clicked.
   *
   * When OMITTED the View affordance is not rendered at all — the page passes it
   * only once the data the drill needs has loaded, so the affordance is gated the
   * same way LocAvailabilityPage gates its own.
   */
  onViewLocUsage?: (column: CashPositionMultiPeriodColumn, value: number) => void
  /**
   * Missing-data warnings per column (DCP-161), keyed by `column.key`, `pack:<id>`
   * and {@link TOTAL_COLUMN_KEY}. Each entry names the rows it applies to, so the
   * grid only has to look up the row's own `warningKey`.
   *
   * OMITTED → no VISIBLE icons at all, which is how every other consumer leaves
   * the report unannotated. Note this is not the pre-DCP-161 DOM: the two rows
   * carrying a `warningKey` (Bank Balances, Intacct Balance) wrap their value in a
   * flex span and reserve an icon-width spacer regardless of this prop — see
   * {@link CellWarningSlot} for why the width is reserved unconditionally. That is
   * why tests predating DCP-161 assert against the cell (`.closest('td')`) rather
   * than the text node.
   */
  warnings?: CashPositionWarningMap
}

/**
 * Renders a value cell's missing-data warning (DCP-161) — which sits AFTER the
 * number, per the BA prototype — or an invisible spacer of the same size when the
 * cell is clean.
 *
 * The spacer is the load-bearing part of this component, and the invariant it
 * encodes is: every cell of a row that carries a `warningKey` reserves an
 * icon-width slot UNCONDITIONALLY. Two things depend on that.
 *  1. Horizontally: the table is `table-layout: auto`, so a cell that gains an
 *     icon widens its whole column. Without a reserved slot, fixing one facility's
 *     balance would re-flow every column to its right mid-scroll and re-trigger the
 *     label-column `ResizeObserver`.
 *  2. Within the row: because the icon TRAILS the value, an unreserved slot would
 *     let a clean cell's digits sit further right than a flagged neighbour's. With
 *     the slot always present, every figure on the row shares the same offset and
 *     the numbers stay mutually comparable whatever the warning map says.
 * Either way the layout is a function of the row list, never of the data.
 *
 * @param warning - the warning text, or undefined when the cell is fine
 * @param label - the accessible name; column-scoped, since one row can carry many
 *   icons and "Warning: …" alone would be ambiguous to a screen reader
 */
function CellWarningSlot({ warning, label }: { warning?: string; label: string }) {
  if (!warning) return <span aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
  return (
    <DataWarningIcon
      message={warning}
      label={label}
      side="bottom"
      className="h-3.5 w-3.5 shrink-0"
    />
  )
}

/**
 * Baseline widths (px) for the two LEFT-frozen columns (DCP-143):
 *
 *   - `COL_LABEL_W` — the row-label column. Wide enough that no label wraps or
 *     clips; the longest labels are "Inter-Co Loan/LOC Projections",
 *     "Minimum Daily Bank Balance", and "EXPECTED EOW BOOK BALANCE". 264px clears
 *     them at the pl-6 indent (label cells also carry `whitespace-nowrap`).
 *   - `COL_TOTAL_W` — the frozen "Combined Total" column.
 *
 * These are a FLOOR and a starting value, NOT the authority on the freeze offset
 * (DCP-293). The table is `table-layout: auto`, where a `<colgroup>` width is only
 * a suggestion: the used width is at least the column's max-content width, so a
 * long bold-uppercase label can push the label column past 264px. The Combined
 * Total is the SECOND frozen column, so pinning it at a hardcoded
 * `left: COL_LABEL_W` would then park it ON TOP of the label column and its
 * figures would overlap the row labels during horizontal scroll. The offset is
 * therefore MEASURED from the rendered header cell (see `labelWidth` below) and
 * `COL_LABEL_W` is additionally applied as a `minWidth` on the label cells so the
 * column can never shrink below the baseline either.
 *
 * The offset is applied as an INLINE style rather than a Tailwind class because it
 * is a runtime value (and even for the constant, JIT only detects class names
 * present verbatim in source).
 */
const COL_LABEL_W = 264
const COL_TOTAL_W = 150

/**
 * Z-index ladder for the frozen-COLUMN spreadsheet layout. Only the LEFT edge
 * columns are frozen (there is NO frozen header row / vertical freeze, and
 * NOTHING is frozen on the right — facilities simply scroll off), so the ladder
 * is intentionally shallow — a frozen cell only ever needs to sit above the
 * plain scrolling facility body cells:
 *
 *   20  the left-frozen columns (row labels + Combined Total) and the full-width
 *       section-heading cell.
 *   --  plain facility body cells: no sticky, default stacking.
 *
 * The frozen columns are adjacent and never overlap (label at left-0, Combined
 * Total at the label column's MEASURED width — see {@link COL_LABEL_W}), so they
 * share one z-level; equal z is fine and no intra-freeze stacking order is needed.
 */
const Z_FROZEN_COL = 'z-20'

/**
 * Frozen-edge affordance: a soft inset box-shadow (arbitrary value) on the OUTER
 * edge of the frozen block so it reads as floating over the scrolling content.
 * That outer edge is whichever frozen column borders the scrolling facilities:
 * the Combined Total's RIGHT edge normally, or the LABEL column's right edge when
 * the Combined Total is hidden (DCP-293). Kept subtle — a soft ~1px shadow beyond
 * the cell's border. The inner boundary between two frozen columns needs no float
 * shadow — a plain `border-r` separator suffices there.
 */
const SHADOW_FROZEN_EDGE = 'shadow-[inset_-8px_0_6px_-6px_rgba(15,23,42,0.12)]'

/**
 * Section-band tint per tone (heading row). Only the band and a thin label-side
 * accent carry colour — the numeric data field stays neutral so figures pop.
 */
const SECTION_BAND: Record<SectionTone, string> = {
  indigo: 'bg-indigo-50 text-indigo-900',
  // LINE OF CREDIT (DCP-147): the emphasized blue band from the BA mockup
  // (#dbeafe / #1e40af) — deliberately more saturated than the amber-50 period
  // bands so the LOC block reads as the highlighted summary section.
  blue: 'bg-blue-100 text-blue-800',
  amber: 'bg-amber-50 text-amber-900',
}

/**
 * Per-tone section-heading typography. The LINE OF CREDIT block (blue, DCP-147)
 * gets the emphasized heading — larger (15px) and heavier with a blue left rule,
 * matching the BA mockup (#2563eb, 15px). The amber period headings keep the
 * compact 12px treatment. `indigo` carries no heading row (the balances block is
 * headerless) but is listed for exhaustiveness over the tone union.
 */
const SECTION_HEADING: Record<SectionTone, string> = {
  indigo: 'py-1.5 text-xs font-semibold',
  blue: 'border-l-[3px] border-blue-600 py-2 text-[15px] font-bold',
  amber: 'py-1.5 text-xs font-semibold',
}

/**
 * Thin left accent on each row's sticky label cell, tying the row back to its
 * section hue without tinting the number field.
 */
const LABEL_ACCENT: Record<SectionTone, string> = {
  indigo: 'border-l-2 border-indigo-200',
  blue: 'border-l-2 border-blue-300',
  amber: 'border-l-2 border-amber-200',
}

/**
 * Multi-period Cash Position Summary grid (DCP-143 / DCP-144 / DCP-293).
 *
 * Renders the row-label column first, the "Combined Total" column SECOND (both
 * left-frozen; the total column can be hidden via `showCombinedTotal`), then one
 * value cell per facility/pack scrolling to their right.
 * The body is composed of stacked BLOCKS:
 *   1. The period-independent balances block ({@link buildBalancesRows}), always
 *      rendered once and fed each column's period-0 summary.
 *   2. The period-independent LINE OF CREDIT block ({@link buildLocRows},
 *      DCP-147), also rendered once from period 0, between balances and the first
 *      projection period.
 *   3. One projection block ({@link buildProjectionRows}) per visible period, in
 *      the order given by `visiblePeriodIndices`, each fed that period's summary
 *      from every column.
 * Zebra striping resets at the top of every block so each block stripes
 * consistently regardless of the preceding block's row count.
 *
 * Layout / scrolling — HORIZONTAL ONLY, inside the card:
 *   - The scroll container is `overflow-x-auto` and the table is
 *     `w-full min-w-max`, so it stretches to fill spare container width (few
 *     facilities / collapsed sidebar) yet never shrinks below its content —
 *     when the columns exceed the container the grid scrolls left/right WITHIN
 *     the card and the page body never widens or scrolls horizontally. The header
 *     row is NOT sticky (it scrolls away with the body).
 *   - The LEFT-most columns are frozen: the label column (`sticky left-0`) and,
 *     when shown, the Combined Total column (sticky at the label column's
 *     MEASURED width, directly abutting it — see {@link COL_LABEL_W} for why the
 *     offset is measured rather than hardcoded). Nothing is frozen on the right.
 *     See {@link Z_FROZEN_COL} for stacking.
 *
 * Opaque-background technique (critical for sticky cells): sticky cells need an
 * opaque background or the horizontally-scrolling cells bleed through them.
 * Instead of a per-cell background we set the background on the `<tr>` (base /
 * zebra / total / caps / hover) and give every cell `bg-inherit`. The frozen
 * columns then inherit the row's background — including the hover state. The one
 * exception is the pack-subtotal band (DCP-293), which layers a TRANSLUCENT tint
 * over the row background; those cells are not sticky, so nothing can bleed
 * through them.
 *
 * Presentation-only — all math (including period chaining) lives in the RPC.
 */
export function CashPositionSummaryGrid({
  columns,
  combinedTotal,
  periodMeta,
  visiblePeriodIndices,
  onViewLocUsage,
  showCombinedTotal = true,
  warnings,
}: CashPositionSummaryGridProps) {
  // Number of data value columns — facilities AND pack subtotals (excludes the
  // sticky label + Combined Total columns).
  const dataColCount = columns.length
  // How many LEFT-frozen columns there are: the label column, plus the Combined
  // Total when shown. Drives the section band's colSpan (DCP-293).
  const frozenColCount = showCombinedTotal ? 2 : 1

  /**
   * Measured width of the rendered label column, driving the Combined Total's
   * sticky `left` offset so the two frozen columns always abut EXACTLY (DCP-293).
   * See {@link COL_LABEL_W} for why the constant alone can't be trusted.
   *
   * Seeded with the baseline so the very first paint (and jsdom, which has no
   * ResizeObserver) matches the previous hardcoded behaviour. Measuring the label
   * column can't feed back into its own width — the observed cell is to the LEFT
   * of the cell being offset — so there is no resize loop.
   */
  const labelHeaderRef = useRef<HTMLTableCellElement>(null)
  const [labelWidth, setLabelWidth] = useState(COL_LABEL_W)

  useLayoutEffect(() => {
    const cell = labelHeaderRef.current
    if (!cell || typeof ResizeObserver === 'undefined') return

    // Sub-pixel churn would re-render on every scroll-driven relayout, so only
    // commit changes the freeze can actually be misaligned by.
    const sync = () => {
      const width = cell.getBoundingClientRect().width
      setLabelWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width))
    }

    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(cell)
    return () => observer.disconnect()
    // Re-measure when the column set changes: auto table layout redistributes
    // width, which can resize the label column.
  }, [dataColCount, showCombinedTotal])

  const balancesRows = buildBalancesRows()
  // Built once (like balancesRows) rather than inline in JSX, so a grid re-render
  // doesn't reallocate the row list + selector closures each time (DCP-147).
  const locRows = buildLocRows()

  /**
   * Renders one contiguous block of rows for a single period index, fed the
   * matching per-period summary from every column and the Combined Total.
   *
   * Zebra parity is counted over `value` rows ONLY (section/total/caps rows keep
   * their own bg and don't stripe), and is LOCAL to the block — a fresh counter
   * per block keeps each block's stripes consistent even though the balances and
   * projection blocks have different value-row counts. We track the running index
   * of value rows here rather than using CSS `odd:`/`even:`, because nth-child
   * would count the interrupting section/total rows too.
   *
   * @param blockRows - the row descriptors for this block
   * @param periodIdx - which period's summary each row reads
   * @param keyPrefix - unique key namespace so repeated labels across blocks stay
   *   distinct React keys
   */
  const renderBlock = (
    blockRows: SummaryRow[],
    periodIdx: number,
    keyPrefix: string,
  ): ReactNode[] => {
    let valueRowIndex = 0

    return blockRows.map((row, index) => {
      if (row.kind === 'section') {
        // Section heading: a full-width band spanning every column, tinted per
        // section tone. The band cell itself can't be `sticky left-0` — a cell
        // that already spans the full scroll width has zero sticky travel, so it
        // would just scroll away with the body. Instead the cell holds only the
        // (full-width) band, and an inline-block child pins the heading TEXT to
        // the left edge: being content-width it has room to stick, so the label
        // ("Remainder of Week …") stays readable while the columns scroll (DCP-306).
        return (
          <tr key={`${keyPrefix}-section-${row.label}`} className={SECTION_BAND[row.tone]}>
            <th
              colSpan={dataColCount + frozenColCount}
              scope="colgroup"
              className="border-y border-slate-200 bg-inherit p-0 text-left"
            >
              <span
                className={cn(
                  'sticky left-0 inline-block px-4 uppercase tracking-wider',
                  SECTION_HEADING[row.tone],
                  Z_FROZEN_COL,
                )}
              >
                {row.label}
              </span>
            </th>
          </tr>
        )
      }

      const isTotal = row.kind === 'total'
      const isCaps = isTotal && row.emphasis === 'caps'
      // The block's bottom-line ending row gets semantic sign colouring on its
      // numbers. Flag-driven (DCP-144), NOT label-matched, because the label
      // varies per period ("Expected EOW Book Balance" vs "Ending Book Balance").
      const isSemanticEow = isTotal && row.semanticEow === true
      // The two emphasized subtotals inside the amber block (Projected Inflows /
      // Outflows). Flag-driven too, so row reorders can't misattribute the band.
      const isProjectedSubtotal = isTotal && row.projectedSubtotal === true
      const label = isCaps ? row.label.toUpperCase() : row.label

      // Zebra parity over value rows only (see the counter note above).
      const isValue = row.kind === 'value'
      const isZebraOdd = isValue && valueRowIndex % 2 === 1
      if (isValue) valueRowIndex += 1

      // DCP-169: a drillable value row (LOC Usage) exposes a per-column "View"
      // affordance. Flag-driven (`row.drillable`), NEVER label-matched. Only
      // active when a handler is provided — the page passes it once
      // `useLocAvailability` (the starting balances the drill's Total needs) has
      // resolved (DCP-333) — which also narrows `row` to the value variant so
      // `row.drillable` is type-safe to read here.
      const isDrillable = row.kind === 'value' && row.drillable === true && onViewLocUsage != null

      // DCP-161: which missing-data signal (if any) this row surfaces per cell.
      // Flag-driven like `drillable`; a row without the flag renders exactly as
      // before, spacer included.
      const warningKey = row.kind === 'value' ? row.warningKey : undefined
      // Read once — it feeds both the icon and its accessible name below.
      const totalWarning = warningKey ? warnings?.get(TOTAL_COLUMN_KEY)?.[warningKey] : undefined

      // Row-level background drives the frozen cells via `bg-inherit`. Hover wins
      // over base/zebra (state variant beats plain utility).
      // - caps: the punchline ending row gets the strongest resting band
      //   (slate-100); the other caps row (Book Balance) a lighter slate-50.
      // - projected subtotals: a light amber band that reads as an emphasized
      //   subtotal — distinct in HUE from the slate caps rows and more saturated
      //   than the amber-50 section band, so all three bands stay separable.
      //   MUST be OPAQUE (not amber-100/60): the frozen label + Combined Total
      //   cells inherit this row bg, and a translucent tint let the scrolling
      //   data cells bleed through them during horizontal scroll (DCP-306).
      // - total (non-caps, other): plain white (borders alone set them off).
      // - value: white / slate-50 zebra.
      const rowBg = cn(
        'transition-colors hover:bg-blue-50',
        isCaps
          ? isSemanticEow
            ? 'bg-slate-100'
            : 'bg-slate-50'
          : isProjectedSubtotal
            ? 'bg-amber-100'
            : isTotal
              ? 'bg-white'
              : isZebraOdd
                ? 'bg-slate-50'
                : 'bg-white',
      )

      // Row borders encode hierarchy: the ending punchline gets the heaviest
      // bottom rule (echoing the accounting double-underline); caps get a thick
      // top rule; plain totals a single top rule; value rows a hairline bottom.
      const rowBorder = cn(
        isCaps
          ? 'border-t-2 border-slate-400'
          : isTotal
            ? 'border-t border-slate-300'
            : 'border-b border-slate-100',
        isSemanticEow && 'border-b-2 border-slate-400',
      )

      // Label indent encodes hierarchy: line items sit under their section
      // (pl-6), totals a step out (pl-4), caps flush at pl-4.
      const labelIndent = isValue ? 'pl-6' : 'pl-4'

      // Per-row Combined Total value for this period. Only read when the column
      // is actually rendered (DCP-293).
      const totalValue = showCombinedTotal ? row.field(combinedTotal.periods[periodIdx]) : 0

      return (
        <tr key={`${keyPrefix}-${row.kind}-${row.label}-${index}`} className={cn(rowBg, rowBorder)}>
          {/* Frozen row-label cell (first frozen column, left-0) — inherits the
              row bg (incl. hover) and carries the thin section-hue accent on its
              left edge. `whitespace-nowrap` so the column never wraps a long
              label; `minWidth` holds the baseline width (see COL_LABEL_W). Its
              right edge is a plain separator when the frozen Combined Total sits
              beside it, and the outer frozen edge (heavier rule + float shadow)
              when that column is hidden (DCP-293). */}
          <th
            scope="row"
            style={{ minWidth: COL_LABEL_W }}
            className={cn(
              'sticky left-0 whitespace-nowrap bg-inherit py-1.5 pr-4 text-left font-normal text-slate-700',
              showCombinedTotal
                ? 'border-r border-slate-200'
                : cn('border-r-2 border-r-slate-300', SHADOW_FROZEN_EDGE),
              labelIndent,
              LABEL_ACCENT[row.tone],
              Z_FROZEN_COL,
              isTotal && 'font-semibold text-slate-800',
              // Projected subtotals read as emphasized subtotals: fully bold.
              isProjectedSubtotal && 'font-bold',
              isCaps && 'font-bold text-slate-900',
            )}
          >
            {label}
          </th>

          {/* Frozen Combined Total cell (SECOND frozen column, left = the MEASURED
              label-column width, applied inline) — the answer column: heavier
              right rule + float shadow on its outer (right) edge, bolder text. NO
              competing bg (bg-inherit) so the row-bg hover survives. */}
          {showCombinedTotal && (
            <td
              style={{ left: labelWidth }}
              className={cn(
                'sticky border-l border-slate-200 border-r-2 border-r-slate-300 bg-inherit px-4 py-1.5 text-right font-semibold tabular-nums lining-nums',
                Z_FROZEN_COL,
                SHADOW_FROZEN_EDGE,
                cellSignColor(totalValue, isSemanticEow),
                isProjectedSubtotal && 'font-bold',
                isCaps && 'font-bold',
              )}
            >
              {warningKey ? (
                // DCP-161: the Combined Total aggregates every visible facility, so
                // it warns whenever ANY of them is missing data — otherwise a total
                // computed from an incomplete scope would look authoritative.
                // Value FIRST, warning slot after it (BA prototype). The slot is
                // reserved even when this column is clean so the total keeps the
                // same offset as the facility cells on the same row — see
                // {@link CellWarningSlot} for the full invariant.
                <span className="flex items-center justify-end gap-1">
                  {formatCell(totalValue)}
                  <CellWarningSlot
                    warning={totalWarning}
                    label={`Warning: Combined Total — ${totalWarning ?? ''}`}
                  />
                </span>
              ) : (
                formatCell(totalValue)
              )}
            </td>
          )}

          {/* One value cell per data column — reading this period's summary. Thin
              right separator guides top-to-bottom reading; bg-inherit for uniform
              hover. Both tabular-nums + lining-nums so digit columns align.
              Pack subtotal columns are HIGHLIGHTED as a full-height band (DCP-293):
              heavier dividers on BOTH sides, a semibold value, and a slate tint.
              The tint is deliberately TRANSLUCENT so the row's own background —
              zebra, the amber subtotal band, the slate caps bands and the blue
              hover — still reads through it, making the column an accent rather
              than a flat override. Translucency is safe HERE only because these
              cells are not sticky: the opaque-bg rule (see the component doc)
              applies to the frozen columns, which stay on `bg-inherit`. */}
          {columns.map((col) => {
            const value = row.field(col.periods[periodIdx])
            const isPack = col.kind === 'pack'
            // DCP-169: the View affordance shows on a drillable row's facility/
            // pack cell ONLY when its usage is non-zero and non-null — mirroring
            // the prototype's `canDrill = value !== 0 && type !== 'combined'`
            // (the Combined Total is a separate frozen cell above, never here).
            const showView = isDrillable && value != null && value !== 0
            // DCP-161: this column's warning for this row, if the row carries a
            // signal at all. Pack columns get their members' summed verdict, so a
            // pack subtotal built from incomplete data is flagged too.
            const warning = warningKey ? warnings?.get(col.key)?.[warningKey] : undefined
            return (
              <td
                key={col.key}
                className={cn(
                  'border-r border-slate-100 px-4 py-1.5 text-right tabular-nums lining-nums',
                  isPack
                    ? 'border-l-2 border-l-slate-300 border-r-2 border-r-slate-300 bg-slate-200/60'
                    : 'bg-inherit',
                  cellSignColor(value, isSemanticEow),
                  isTotal && 'font-semibold',
                  isProjectedSubtotal && 'font-bold',
                  isCaps && 'font-bold',
                  // Pack subtotal columns read fully bold top-to-bottom (DCP-314),
                  // one step above the facility columns beside them. Applied LAST
                  // on purpose: `cn` is twMerge, so a later class wins — putting
                  // it here keeps the pack column bold on total rows too, instead
                  // of being downgraded to semibold by the `isTotal` rule above.
                  isPack && 'font-bold',
                )}
              >
                {showView || warningKey ? (
                  // One adornment row so the two affordances COMPOSE rather than
                  // compete: the VALUE leads, then the missing-data icon (DCP-161),
                  // then the LOC drill-down button (DCP-169). The warning trails the
                  // number per the BA prototype, and every cell of a flagged row
                  // reserves the icon-width slot whether or not it is warned — so
                  // the row's figures stay mutually aligned and clearing one
                  // facility's data can never re-width the column mid-scroll (see
                  // {@link CellWarningSlot}).
                  // The two flags cannot coincide with today's row list — LOC Usage
                  // is the only `drillable` row and carries no `warningKey`, and
                  // neither warned row is drillable (cashPositionRows.ts) — but the
                  // order is pinned here so a row that is one day both renders
                  // predictably rather than however JSX happens to fall out.
                  // onViewLocUsage is defined whenever showView is (isDrillable
                  // requires it), so the optional chain below never no-ops.
                  <span className="flex items-center justify-end gap-1">
                    {formatCell(value)}
                    {warningKey && (
                      <CellWarningSlot
                        warning={warning}
                        label={`Warning: ${col.name} — ${warning ?? ''}`}
                      />
                    )}
                    {showView && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        // `value` is the drilled LOC Usage figure the modal
                        // reconciles its transactions against (DCP-333). No
                        // narrowing needed: `showView` already asserts it is
                        // non-null and non-zero, and TS carries that through this
                        // closure because both it and `value` are const.
                        onClick={() => onViewLocUsage?.(col, value)}
                        // Per-column label so multiple View buttons (one per
                        // facility/pack column with non-zero usage) are
                        // distinguishable for screen readers, not all identically
                        // "View LOC transactions" (PR #169 review).
                        aria-label={`View LOC transactions for ${col.name}`}
                        // h-6 w-6 to match the same View button on LocAvailabilityPage
                        // (visual consistency across the two pages — PR #169 review).
                        className="h-6 w-6 shrink-0"
                      >
                        <ViewTransactionsIcon className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </span>
                ) : (
                  formatCell(value)
                )}
              </td>
            )
          })}
        </tr>
      )
    })
  }

  return (
    // Own TooltipProvider so facility-name tooltips work regardless of an
    // ancestor provider (the app-level one in main.tsx still applies too);
    // Radix supports nested providers.
    <TooltipProvider delayDuration={150}>
      <div className="space-y-2">
        {/* Horizontal-only scroll container — the wide grid scrolls inside here so
            the page body never scrolls horizontally. No vertical scroll. */}
        <div className="overflow-x-auto rounded-md border border-slate-200">
          {/* `w-full min-w-max`: stretch to fill the container when there's spare
              width (few facilities, or a collapsed sidebar) so no empty gutter is
              left on the right; but never shrink below the intrinsic content width
              — once the columns need more room than the container, min-w-max
              forces that width and the wrapper's overflow-x scroll takes over. */}
          <table className="w-full min-w-max border-collapse text-sm">
            {/* Explicit column widths so the second frozen column's inline `left`
                offset (= COL_LABEL_W) lands exactly on the label column's edge.
                Data columns are left auto/unset so they size to content. */}
            <colgroup>
              <col style={{ width: COL_LABEL_W }} />
              {showCombinedTotal && <col style={{ width: COL_TOTAL_W }} />}
              {columns.map((col) => (
                <col key={col.key} />
              ))}
            </colgroup>
            <thead>
              {/* Static header row (NOT sticky): column labels scroll with body. */}
              <tr className="bg-slate-50">
                {/* Top-left label corner: frozen left (left-0). The MEASURED cell
                    (see `labelWidth`) — its rendered width is what the Combined
                    Total's sticky offset is derived from. Plain right border as a
                    separator while the Combined Total beside it is also frozen;
                    the outer frozen edge (heavier rule + float shadow) when that
                    column is hidden (DCP-293). */}
                <th
                  scope="col"
                  ref={labelHeaderRef}
                  style={{ minWidth: COL_LABEL_W }}
                  className={cn(
                    'sticky left-0 bg-slate-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500',
                    showCombinedTotal
                      ? 'border-r border-slate-200'
                      : cn('border-r-2 border-r-slate-300', SHADOW_FROZEN_EDGE),
                    Z_FROZEN_COL,
                  )}
                />
                {/* Combined Total corner: SECOND frozen column (left = the measured
                    label-column width, applied inline). Stronger type to mark the
                    summary column. Its RIGHT edge is the outer frozen edge, so it
                    carries the float shadow + a subtle left divider from the label
                    column. */}
                {showCombinedTotal && (
                  <th
                    scope="col"
                    style={{ left: labelWidth }}
                    className={cn(
                      'sticky border-l border-slate-200 border-r-2 border-r-slate-300 bg-slate-50 px-4 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-700',
                      Z_FROZEN_COL,
                      SHADOW_FROZEN_EDGE,
                    )}
                  >
                    Combined Total
                  </th>
                )}
                {columns.map((col) =>
                  col.kind === 'pack' ? (
                    // Pack subtotal column header (DCP-145): pack name + a "Total"
                    // sublabel. Caps the highlighted pack band (DCP-293) — the
                    // strongest tint of the three header tones and the same
                    // heavier dividers on BOTH sides as the band's body cells, so
                    // the column reads as one continuous highlight.
                    <th
                      key={col.key}
                      scope="col"
                      className="max-w-[160px] border-l-2 border-l-slate-300 border-r-2 border-r-slate-300 bg-slate-200 px-4 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-700"
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block cursor-default truncate">{col.name}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top">{col.name} — pack total</TooltipContent>
                      </Tooltip>
                      <span className="block text-[10px] font-medium normal-case tracking-normal text-slate-500">
                        Total
                      </span>
                    </th>
                  ) : (
                    // Facility column header. Cap width + truncate so long names
                    // never blow out a column; the full name shows in a hover
                    // tooltip. The truncating <span> is the tooltip trigger.
                    // Bold + slate-700, matching the pack and Combined Total
                    // headers (DCP-314): every column name now carries the same
                    // weight, and the column hierarchy is carried by the band
                    // tint / dividers instead of by header weight.
                    <th
                      key={col.key}
                      scope="col"
                      className="max-w-[160px] border-r border-slate-100 bg-slate-50 px-4 py-2 text-right text-xs font-bold uppercase tracking-wider text-slate-700"
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block cursor-default truncate">{col.name}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top">{col.name}</TooltipContent>
                      </Tooltip>
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {/* Balances block — rendered once, always from period 0 (these
                  figures are period-independent). */}
              {renderBlock(balancesRows, 0, 'balances')}

              {/* LINE OF CREDIT block (DCP-147) — also period-independent, so
                  rendered once from period 0, after balances and before the
                  first projection period. */}
              {renderBlock(locRows, 0, 'loc')}

              {/* One projection block per visible period, in the selected order. */}
              {visiblePeriodIndices.map((periodIdx) =>
                renderBlock(
                  buildProjectionRows(periodMeta[periodIdx]),
                  periodIdx,
                  `period-${periodIdx}`,
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </TooltipProvider>
  )
}

/**
 * Picks the sign colour for a value cell.
 *
 *  - Null (a dashed LOC cell, DCP-147) renders muted slate (`text-slate-400`),
 *    same as zero — a missing value is neutral, never red/green.
 *  - Zero renders muted slate (`text-slate-400`): still clearly visible as "$0"
 *    but de-emphasized so real figures pop across a wide, mostly-sparse grid.
 *  - The block's bottom-line ending row is semantic: positive is good (emerald),
 *    negative is bad (red).
 *  - Every other row uses the restrained scheme: negatives red, positives
 *    neutral slate (no green flood).
 *
 * @param value - the signed cell value, or null for a dashed (no-data) cell
 * @param semantic - whether this cell belongs to the semantic ending row
 * @returns the Tailwind text-colour class for the cell
 */
function cellSignColor(value: number | null, semantic: boolean): string {
  if (value == null || value === 0) return 'text-slate-400'
  if (semantic) return value < 0 ? 'text-red-700' : 'text-emerald-700'
  return value < 0 ? 'text-red-700' : 'text-slate-900'
}
